import * as React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useNotificationStore } from "@/store"
import { api } from "@/lib/api"
import { PageLoader, ErrorState } from "@/components/PageStates"
import { Save, Loader2 } from "lucide-react"

function SettingsPage() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data: settings, isLoading, isError, refetch } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get("/settings"),
  })

  const updateMutation = useMutation({
    mutationFn: (data) => api.post("/settings/batch", { settings: data }),
    onSuccess: () => {
      queryClient.invalidateQueries(["settings"])
      addNotification({ type: "success", message: "Settings saved successfully" })
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  const [formData, setFormData] = React.useState({})

  React.useEffect(() => {
    if (settings?.data) {
      const flat = {}
      Object.entries(settings.data).forEach(([module, items]) => {
        items.forEach((item) => {
          flat[item.key] = item.value
        })
      })
      setFormData(flat)
    }
  }, [settings])

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = () => {
    const settingsArray = Object.entries(formData).map(([key, value]) => ({
      key,
      value,
    }))
    updateMutation.mutate(settingsArray)
  }

  const settingGroups = {
    general: [
      { key: "bot_name", label: "Bot Name", description: "Display name of the bot" },
      { key: "bot_username", label: "Bot Username", description: "Telegram bot username" },
      { key: "support_id", label: "Support ID", description: "Support contact" },
      { key: "channel_id", label: "Channel ID", description: "Main channel" },
      { key: "group_id", label: "Group ID", description: "Main group" },
      { key: "timezone", label: "Timezone", description: "System timezone" },
      { key: "default_language", label: "Default Language", description: "Default language" },
    ],
    payment: [
      { key: "card_number", label: "Card Number", description: "Payment card number" },
      { key: "card_owner", label: "Card Owner", description: "Card holder name" },
      { key: "card_bank", label: "Bank Name", description: "Bank name" },
      { key: "invoice_expire_minutes", label: "Invoice Expiry (min)", description: "Invoice expiration time" },
      { key: "payment_text", label: "Payment Text", description: "Payment instruction text" },
    ],
    scheduler: [
      { key: "subscription_scan_hours", label: "Subscription Scan (hours)", description: "How often to scan subscriptions" },
      { key: "ip_scan_hours", label: "IP Scan (hours)", description: "How often to scan IPs" },
      { key: "stable_scan_hours", label: "Stable Scan (hours)", description: "How often to scan stable configs" },
      { key: "proxy_post_hours", label: "Proxy Post (hours)", description: "How often to post proxies" },
    ],
  }

  if (isLoading) return <PageLoader message="Loading settings..." />
  if (isError) return <ErrorState message="Failed to load settings." onRetry={() => refetch()} />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">Configure your system settings</p>
        </div>
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
          <TabsTrigger value="scheduler">Scheduler</TabsTrigger>
        </TabsList>

        {Object.entries(settingGroups).map(([module, fields]) => (
          <TabsContent key={module} value={module} className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="capitalize">{module} Settings</CardTitle>
                <CardDescription>
                  Configure {module} related settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key} className="grid gap-2">
                    <label className="text-sm font-medium">{field.label}</label>
                    <Input
                      value={formData[field.key] || ""}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={field.description}
                    />
                    <p className="text-xs text-muted-foreground">{field.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

export default SettingsPage
